import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";
import { useI18n } from "../lib/i18n";
import FinPipMark from "./FinPipMark";

const DISMISS_KEY = "journal.installDismissed";

function isStandalone(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !/crios|fxios/i.test(navigator.userAgent);
}

/**
 * PWA install banner — shown once to a logged-in user (mounted in Shell) when
 * the app is installable and not already installed/dismissed. On Chromium it
 * uses the captured `beforeinstallprompt` event (see main.tsx); on iOS Safari,
 * which has no such event, it shows the manual "Add to Home Screen" hint.
 * Dismissal is remembered in localStorage so it doesn't nag.
 */
export default function InstallBanner() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed → never show
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") return;
    } catch {
      /* ignore */
    }

    // iOS has no beforeinstallprompt — show the manual hint instead.
    if (isIos()) {
      setIos(true);
      setVisible(true);
      return;
    }

    // Chromium: show if the prompt is already captured, else wait for it.
    if (window.deferredInstallPrompt) setVisible(true);
    const onInstallable = () => setVisible(true);
    const onInstalled = () => setVisible(false);
    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  function dismiss() {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  }

  async function install() {
    const evt = window.deferredInstallPrompt;
    if (!evt) return;
    await evt.prompt();
    try {
      await evt.userChoice;
    } catch {
      /* ignore */
    }
    window.deferredInstallPrompt = undefined;
    dismiss();
  }

  if (!visible) return null;

  return (
    <div
      className="rounded-2xl p-4 flex items-center gap-4 flex-wrap"
      style={{
        backgroundColor: "rgb(var(--bg-800))",
        border: "1px solid rgb(var(--border-mid))",
        boxShadow: "var(--shadow-glass)",
      }}
    >
      <span
        className="grid place-items-center w-11 h-11 rounded-xl shrink-0"
        style={{ backgroundColor: "rgb(var(--glow) / 0.16)" }}
      >
        <FinPipMark className="w-6 h-6 text-[#0c34f4] dark:text-white" />
      </span>
      <div className="flex-1 min-w-[180px]">
        <p className="font-bold text-parchment">{t("install.title")}</p>
        <p className="text-sm text-parchment-muted">{ios ? t("install.iosHint") : t("install.body")}</p>
      </div>
      <div className="flex items-center gap-2 ms-auto">
        {ios ? (
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent px-2">
            <Share className="w-4 h-4" />
          </span>
        ) : (
          <button type="button" onClick={install} className="btn-gold">
            <Download className="w-4 h-4" />
            {t("install.button")}
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          className="text-parchment-muted hover:text-parchment p-2 -m-1"
          aria-label={t("install.dismiss")}
          title={t("install.dismiss")}
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
